import { Immutability } from "@bitory-gmbh/nidavellir";
import { Artefact, Document } from "@bitory-gmbh/bsm";
import { Process } from "@/services/backend/processes/types/process";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import {
  newPositionFrom,
  Position,
} from "@/services/backend/htz/position/types/position";
import { v4 } from "uuid";
import {
  Address,
  newAddress,
} from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import { Common } from "@/services/backend/documents/types/common";

export interface Offer extends Document, Artefact, Immutability, Common {
  date: string | null;
  positions: Position[];
  specialAgreements: string;
  postalAddress: Address;
  serviceAddress: Address;
  contact: Contact;
  representative: Representative;
}

export function newOffer(
  process: Process,
  branchId: string,
  serviceObjectId: string,
): Offer {
  return {
    id: v4(),
    documentKind: GSMDocumentKind.Offer,
    processId: process.id,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    immutable: false,
    immutabilityReason: "",
    immutabilitySetAt: undefined,
    immutabilitySetBy: "",
    companyId: process.companyId,
    branchId,
    customerId: process.customerId!,
    serviceObjectId,
    date: new Date().toISOString(),
    positions: [],
    specialAgreements: "",
    postalAddress: newAddress(),
    serviceAddress: newAddress(),
    contact: {
      salutation: "",
      title: "",
      name: "",
      function: "",
      email: "",
      phone: "",
      mobilephone: "",
    } as Contact,
    representative: {
      name: "",
      email: "",
      phone: "",
      mobilephone: "",
    } as Representative,
  };
}

export function newOfferFrom(
  offer: Offer,
  processId: string,
  companyId: string,
  branchId: string,
  customerId: string,
  serviceObjectId: string,
): Offer {
  return {
    id: v4(),
    documentKind: GSMDocumentKind.Offer,
    processId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    immutable: false,
    immutabilityReason: "",
    immutabilitySetAt: undefined,
    immutabilitySetBy: "",
    companyId,
    branchId,
    customerId,
    serviceObjectId,
    date: new Date().toISOString(),
    positions: offer.positions.map((pos) => newPositionFrom(pos)),
    specialAgreements: offer.specialAgreements,
    postalAddress: offer.postalAddress,
    serviceAddress: offer.serviceAddress,
    contact: offer.contact,
    representative: offer.representative,
  };
}

export function updateDate(offer: Offer, date: string | null): Offer {
  return {
    ...offer,
    date,
  };
}

export function updateSpecialAgreements(
  offer: Offer,
  agreements: string,
): Offer {
  return {
    ...offer,
    specialAgreements: agreements,
  };
}

export function addPosition(offer: Offer, position: Position): Offer {
  if (offer.positions.some((pos) => pos.id === position.id)) {
    return offer;
  }

  return {
    ...offer,
    positions: [
      ...offer.positions,
      {
        ...position,
        number: (offer.positions.length + 1).toString(),
      },
    ],
  };
}

export function updatePosition(offer: Offer, position: Position): Offer {
  return {
    ...offer,
    positions: offer.positions.map((pos) => {
      if (pos.id === position.id) {
        return position;
      }
      return pos;
    }),
  };
}

export function deletePosition(offer: Offer, positionId: string): Offer {
  return {
    ...offer,
    positions: offer.positions
      .filter((pos) => pos.id !== positionId)
      .map((pos, idx) => ({
        ...pos,
        number: (idx + 1).toString(),
      })),
  };
}

export function updatePostalAddress(offer: Offer, address: Address): Offer {
  return {
    ...offer,
    postalAddress: address,
  };
}

export function updateServiceAddress(offer: Offer, address: Address): Offer {
  return {
    ...offer,
    serviceAddress: address,
  };
}

export function updateContact(offer: Offer, contact: Contact): Offer {
  return {
    ...offer,
    contact,
  };
}

export function updateRepresentative(
  offer: Offer,
  representative: Representative,
): Offer {
  return {
    ...offer,
    representative,
  };
}
